import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout.js';
import { ChevronDown, X, Check, KeyRound, Shield, Hash, Trash2, RefreshCw, Pencil, CreditCard } from 'lucide-react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { useRoleLabel } from '../hooks/useRoleLabel.js';
import MultiCardEdit from '../components/MultiCardEdit.js';

interface User {
  id: string;
  email: string;
  role: string;
  departmentName?: string | null;
  departmentId?: string | null;
  firstName: string;
  lastName: string;
  created_at: string;
  cardsShortIds: string[];
  cards?: Array<{ slug: string; shortCode: string; orgSlug?: string | null }>;
}

interface Department {
  id: string;
  name: string;
}

type RoleOption = 'member' | 'admin' | 'owner';
const ROLE_OPTIONS: Array<{ value: RoleOption; label: string }> = [
  { value: 'member', label: 'Member' },
  { value: 'admin',  label: 'Verwalter'  },
  { value: 'owner',  label: 'Owner'  },
];

// ─── Shared styles ──────────────────────────────────────────────────────────
const inputCls =
  'block w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition';
const labelCls = 'block text-xs font-medium text-ink-muted mb-1';

const btnSmGhost =
  'inline-flex items-center gap-1 rounded-lg border border-line bg-panel px-2.5 py-1 text-xs text-ink hover:bg-raised hover:text-ink hover:border-line-strong transition-all';
const btnSmDanger =
  'inline-flex items-center gap-1 rounded-lg border border-danger-300 dark:border-danger-500/30 bg-danger-50 dark:bg-danger-500/10 px-2.5 py-1 text-xs text-danger-600 dark:text-danger-300 hover:bg-danger-100 dark:hover:bg-danger-500/20 hover:text-danger-700 dark:hover:text-danger-200 transition-all';
const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-lg bg-accent-600 px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 transition-opacity';

// ─── Role dropdown (inline) ─────────────────────────────────────────────────
function RoleDropdown({
  value,
  onSelect,
  onClose,
}: {
  value: string;
  onSelect: (role: RoleOption) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute right-0 mt-1 w-36 rounded-lg border border-line bg-raised shadow-2xl shadow-black/50 z-20 overflow-hidden"
    >
      {ROLE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition-colors ${
            value === opt.value ? 'bg-accent-100 dark:bg-accent-600/20 text-accent-700 dark:text-accent-200' : 'text-ink hover:bg-raised hover:text-ink'
          }`}
        >
          <span>{opt.label}</span>
          {value === opt.value && <Check className="w-3 h-3" />}
        </button>
      ))}
    </div>
  );
}

// ─── Modal shell (dark glass) ───────────────────────────────────────────────
function GlassModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-raised border border-line shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h3 className="text-sm font-semibold text-ink">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-ink-muted hover:text-ink hover:bg-raised transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function Users() {
  const { t, i18n } = useAppTranslation();
  const navigate = useNavigate();
  const [users, setUsers]             = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');

  // Invite form
  const [showInvite, setShowInvite]       = useState(false);
  const [inviteEmail, setInviteEmail]     = useState('');
  const [inviteRole, setInviteRole]       = useState('member');
  const [inviteDept, setInviteDept]       = useState('');
  const [inviteError, setInviteError]     = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Row-level state
  const [selected, setSelected]               = useState<Set<string>>(new Set());
  const [roleDropdownFor, setRoleDropdownFor] = useState<string | null>(null);
  const [pwdModalFor, setPwdModalFor]         = useState<User | null>(null);
  const [shortModalFor, setShortModalFor]     = useState<User | null>(null);
  const [confirmDelete, setConfirmDelete]     = useState<{ ids: string[]; isBulk: boolean } | null>(null);

  // Bulk state
  const [bulkPwdOpen, setBulkPwdOpen]   = useState(false);
  const [bulkRoleOpen, setBulkRoleOpen] = useState(false);
  const [multiEditOpen, setMultiEditOpen] = useState(false);
  const [busyBulk, setBusyBulk]         = useState(false);
  const [bulkError, setBulkError]       = useState('');

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    refreshAll();
  }, []);

  async function refreshAll() {
    setLoading(true);
    try {
      const [usersData, deptData] = await Promise.all([
        fetch('/api/admin/users', { credentials: 'include' }).then((r) => r.json()),
        fetch('/api/admin/departments', { credentials: 'include' }).then((r) => r.json()),
      ]);
      setUsers(Array.isArray(usersData) ? usersData : usersData.users ?? []);
      setDepartments(deptData.departments ?? []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  // ── Invite ──────────────────────────────────────────────────────────────
  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError('');
    setInviteLoading(true);
    try {
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: inviteEmail,
          password: generatePassword(),
          role: inviteRole,
          departmentId: inviteDept || undefined,
        }),
      });
      const data = (await res.json()) as { success?: boolean; error?: string };
      if (!res.ok || !data.success) {
        setInviteError(data.error ?? t('users.errors.createFailed'));
        return;
      }
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteDept('');
      await refreshAll();
    } catch {
      setInviteError(t('users.errors.networkRetry'));
    } finally {
      setInviteLoading(false);
    }
  }

  // ── Single-row actions ──────────────────────────────────────────────────
  async function changeRole(user: User, role: RoleOption) {
    setRoleDropdownFor(null);
    const res = await fetch(`/api/admin/users/${user.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ role, departmentId: user.departmentId ?? null }),
    });
    if (res.ok) {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role } : u)));
    } else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      alert(data.error ?? t('users.errors.roleChangeFailed'));
    }
  }

  async function setPassword(user: User, password: string) {
    const res = await fetch(`/api/admin/users/${user.id}/password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(data.error ?? t('users.errors.passwordSetFailed'));
    }
  }

  async function setShortCode(user: User, shortCode: string) {
    const firstCard = user.cards?.[0];
    if (!firstCard) throw new Error(t('users.errors.noCardSlug'));

    const res = await fetch(`/api/admin/cards/${firstCard.slug}/short-code`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ userId: user.id, shortCode }),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(data.error ?? t('users.errors.cardIdSetFailed'));
    }
    setUsers((prev) =>
      prev.map((u) =>
        u.id === user.id
          ? { ...u, cardsShortIds: [shortCode, ...(u.cardsShortIds ?? []).slice(1)] }
          : u
      )
    );
  }

  async function deleteUsers(ids: string[]) {
    setBulkError('');
    setBusyBulk(true);
    try {
      const results = await Promise.all(
        ids.map((id) =>
          fetch(`/api/admin/users/${id}`, { method: 'DELETE', credentials: 'include' }).then((r) => ({
            id,
            ok: r.ok,
          }))
        )
      );
      const okIds = new Set(results.filter((r) => r.ok).map((r) => r.id));
      setUsers((prev) => prev.filter((u) => !okIds.has(u.id)));
      setSelected((prev) => {
        const next = new Set(prev);
        okIds.forEach((id) => next.delete(id));
        return next;
      });
      const failed = results.filter((r) => !r.ok).length;
      if (failed > 0) setBulkError(t('users.bulk.deleteFailed', '', { count: failed }));
    } finally {
      setBusyBulk(false);
      setConfirmDelete(null);
    }
  }

  // ── Bulk actions ────────────────────────────────────────────────────────
  async function bulkSetPassword(password: string) {
    setBulkError('');
    setBusyBulk(true);
    try {
      const ids = Array.from(selected);
      const results = await Promise.all(
        ids.map((id) =>
          fetch(`/api/admin/users/${id}/password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ password }),
          }).then((r) => r.ok)
        )
      );
      const failed = results.filter((ok) => !ok).length;
      if (failed > 0) setBulkError(t('users.bulk.passwordFailed', '', { count: failed }));
    } finally {
      setBusyBulk(false);
      setBulkPwdOpen(false);
    }
  }

  async function bulkChangeRole(role: RoleOption) {
    setBulkError('');
    setBusyBulk(true);
    try {
      const ids = Array.from(selected);
      const results = await Promise.all(
        ids.map((id) => {
          const u = users.find((x) => x.id === id);
          return fetch(`/api/admin/users/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ role, departmentId: u?.departmentId ?? null }),
          }).then((r) => ({ id, ok: r.ok }));
        })
      );
      const okIds = new Set(results.filter((r) => r.ok).map((r) => r.id));
      setUsers((prev) => prev.map((u) => (okIds.has(u.id) ? { ...u, role } : u)));
      const failed = results.length - okIds.size;
      if (failed > 0) setBulkError(t('users.bulk.roleFailed', '', { count: failed }));
    } finally {
      setBusyBulk(false);
      setBulkRoleOpen(false);
    }
  }

  // ── Filtering / selection helpers ───────────────────────────────────────
  const filtered = useMemo(
    () =>
      users.filter(
        (u) =>
          u.email.toLowerCase().includes(search.toLowerCase()) ||
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(search.toLowerCase())
      ),
    [users, search]
  );

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.id));
  const someFilteredSelected = filtered.some((u) => selected.has(u.id));
  const selectedUserObjects = useMemo(
    () => users.filter((u) => selected.has(u.id)),
    [users, selected]
  );

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach((u) => next.delete(u.id));
      else filtered.forEach((u) => next.add(u.id));
      return next;
    });
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const selectedCount = selected.size;
  const dateFormatter = new Intl.DateTimeFormat(i18n.language || 'de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Layout title={t('users.pageTitle')}>
      <div className="max-w-6xl space-y-5">
        {/* Bulk action bar */}
        {selectedCount > 0 && (
          <div className="sticky top-2 z-30 rounded-xl border border-accent-500/30 bg-raised/95 backdrop-blur-md shadow-lg shadow-accent-900/20 px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-ink">
              <span className="text-accent-600 dark:text-accent-300">{selectedCount}</span>{' '}
              {t('users.bulk.selected', '', { count: selectedCount })}
            </span>
            <div className="h-4 w-px bg-slate-300 dark:bg-white/10" />
            <button
              onClick={() => setBulkPwdOpen(true)}
              disabled={busyBulk}
              className={btnSmGhost}
            >
              <KeyRound className="w-3 h-3" /> {t('users.bulk.resetPassword')}
            </button>
            <div className="relative">
              <button
                onClick={() => setBulkRoleOpen((v) => !v)}
                disabled={busyBulk}
                className={btnSmGhost}
              >
                <Shield className="w-3 h-3" /> {t('users.bulk.changeRole')}
                <ChevronDown className="w-3 h-3" />
              </button>
              {bulkRoleOpen && (
                <RoleDropdown
                  value=""
                  onSelect={(role) => bulkChangeRole(role)}
                  onClose={() => setBulkRoleOpen(false)}
                />
              )}
            </div>
            <button
              onClick={() => setMultiEditOpen(true)}
              disabled={busyBulk}
              className={btnSmGhost}
            >
              <Pencil className="w-3 h-3" /> {t('users.bulk.editCards', 'Edit cards')}
            </button>
            <button
              onClick={() =>
                setConfirmDelete({ ids: Array.from(selected), isBulk: true })
              }
              disabled={busyBulk}
              className={btnSmDanger}
            >
              <Trash2 className="w-3 h-3" /> {t('common.actions.remove')}
            </button>
            <div className="ml-auto flex items-center gap-3">
              {busyBulk && <RefreshCw className="w-3.5 h-3.5 animate-spin text-ink-muted" />}
              {bulkError && <span className="text-xs text-danger-400">{bulkError}</span>}
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-ink-muted hover:text-ink transition-colors"
              >
                {t('users.bulk.clearSelection')}
              </button>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center gap-4 flex-wrap">
          <input
            type="search"
            placeholder={t('users.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`flex-1 min-w-[200px] sm:max-w-xs ${inputCls}`}
          />
          <button
            onClick={() => setShowInvite((v) => !v)}
            className="rounded-full bg-accent-600 px-5 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            + {t('users.invite')}
          </button>
        </div>

        {/* Invite form */}
        {showInvite && (
          <div className="rounded-2xl bg-panel border border-line p-6">
            <h2 className="mb-4 text-sm font-semibold text-ink">{t('users.createForm.title')}</h2>
            <form
              onSubmit={handleInvite}
              className="flex flex-col sm:flex-row flex-wrap gap-4 items-end"
            >
              <div className="w-full sm:w-64">
                <label className={labelCls}>{t('common.email')}</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className={inputCls}
                  placeholder="colleague@acme.com"
                />
              </div>
              <div className="w-full sm:w-40">
                <label className={labelCls}>{t('common.role')}</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className={`${inputCls} [&>option]:bg-white [&>option]:text-slate-900 dark:[&>option]:bg-panel dark:[&>option]:text-white`}
                >
                  <option value="member">{t('common.roles.member')}</option>
                  <option value="admin">{t('common.roles.admin')}</option>
                  <option value="department_lead">{t('common.roles.departmentLead')}</option>
                </select>
              </div>
              <div className="w-full sm:w-48">
                <label className={labelCls}>{t('common.fields.department')}</label>
                <select
                  value={inviteDept}
                  onChange={(e) => setInviteDept(e.target.value)}
                  className={`${inputCls} [&>option]:bg-white [&>option]:text-slate-900 dark:[&>option]:bg-panel dark:[&>option]:text-white`}
                >
                  <option value="">{t('common.none')}</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="rounded-full bg-accent-600 px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  {inviteLoading ? t('common.states.creating') : t('common.create')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInvite(false)}
                  className="rounded-full border border-line px-5 py-2 text-sm text-ink-muted hover:text-ink hover:border-line-strong transition-all"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
            {inviteError && <p className="mt-3 text-sm text-danger-400">{inviteError}</p>}
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl bg-panel border border-line overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-ink-faint">{t('common.loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-ink-faint">{t('users.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-200/60 dark:bg-white/[0.06] border-b border-line">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected;
                        }}
                        onChange={toggleAll}
                        className="w-4 h-4 rounded border-line-strong bg-panel text-accent-600 focus:ring-accent-500/30 focus:ring-offset-0 cursor-pointer accent-accent-600"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs uppercase font-semibold tracking-wide text-ink-muted">{t('common.email')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase font-semibold tracking-wide text-ink-muted hidden sm:table-cell">{t('common.name')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase font-semibold tracking-wide text-ink-muted hidden md:table-cell">{t('common.role')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase font-semibold tracking-wide text-ink-muted hidden lg:table-cell">{t('common.fields.department')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase font-semibold tracking-wide text-ink-muted hidden lg:table-cell">{t('users.cardId')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase font-semibold tracking-wide text-ink-muted hidden xl:table-cell">{t('users.created')}</th>
                    <th className="text-right px-4 py-3 text-xs uppercase font-semibold tracking-wide text-ink-muted">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                  {filtered.map((u) => {
                    const isSelected = selected.has(u.id);
                    return (
                      <tr
                        key={u.id}
                        className={`transition-colors ${
                          isSelected ? 'bg-accent-500/[0.07]' : 'hover:bg-slate-200/60 dark:hover:bg-white/[0.03]'
                        }`}
                      >
                        <td className="w-10 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOne(u.id)}
                            className="w-4 h-4 rounded border-line-strong bg-panel text-accent-600 focus:ring-accent-500/30 focus:ring-offset-0 cursor-pointer accent-accent-600"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-ink">{u.email}</div>
                          <div className="sm:hidden text-xs text-ink-muted">
                            {u.firstName} {u.lastName}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <div className="text-ink-muted text-xs">
                            {u.firstName} {u.lastName}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <RoleBadge role={u.role} />
                        </td>
                        <td className="px-4 py-3 text-ink-muted text-xs hidden lg:table-cell">
                          {u.departmentName ?? t('users.noneDash')}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <code className="text-xs font-mono text-ink-muted">
                            {u.cardsShortIds?.[0] ?? t('users.noneDash')}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-ink-faint text-xs hidden xl:table-cell">
                          {dateFormatter.format(new Date(u.created_at))}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-1.5 relative min-w-[15rem]">
                            <button
                              onClick={() =>
                                setRoleDropdownFor((cur) => (cur === u.id ? null : u.id))
                              }
                              className={btnSmGhost}
                            >
                              <Shield className="w-3 h-3" /> {t('common.role')}
                            </button>
                            {roleDropdownFor === u.id && (
                              <RoleDropdown
                                value={u.role}
                                onSelect={(r) => changeRole(u, r)}
                                onClose={() => setRoleDropdownFor(null)}
                              />
                            )}
                            <button
                              onClick={() => setPwdModalFor(u)}
                              className={btnSmGhost}
                            >
                              <KeyRound className="w-3 h-3" /> {t('users.password')}
                            </button>
                            <button
                              onClick={() => setShortModalFor(u)}
                              disabled={!u.cards?.[0]}
                              className={`${btnSmGhost} disabled:opacity-40 disabled:cursor-not-allowed`}
                            >
                              <Hash className="w-3 h-3" /> {t('users.cardId')}
                            </button>
                            <button
                              onClick={() => {
                                const card = u.cards?.[0];
                                if (card) {
                                  navigate(`/cards/${card.slug}`);
                                } else {
                                  const slug = [u.firstName, u.lastName]
                                    .filter(Boolean)
                                    .join('-')
                                    .toLowerCase()
                                    .replace(/[^a-z0-9-]/g, '-')
                                    .replace(/-+/g, '-')
                                    .substring(0, 50)
                                    || `card-${u.id.substring(0, 8)}`;
                                  navigate(`/cards/${slug}?userId=${u.id}`);
                                }
                              }}
                              className={btnSmGhost}
                              title={u.cards?.[0] ? t('users.editCard', 'Karte bearbeiten') : t('users.createCard', 'Karte erstellen')}
                            >
                              <CreditCard className="w-3 h-3" />
                              {u.cards?.[0] ? t('users.editCard', 'Karte') : t('users.createCard', '+ Karte')}
                            </button>
                            <button
                              onClick={() =>
                                setConfirmDelete({ ids: [u.id], isBulk: false })
                              }
                              className={btnSmDanger}
                            >
                              <Trash2 className="w-3 h-3" /> {t('common.actions.remove')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Password modal (single) ─────────────────────────────────────── */}
      {pwdModalFor && (
        <PasswordModal
          title={t('users.passwordModal.singleTitle', '', { email: pwdModalFor.email })}
          onClose={() => setPwdModalFor(null)}
          onSubmit={async (pw) => {
            try {
              await setPassword(pwdModalFor, pw);
              setPwdModalFor(null);
            } catch (err) {
              throw err;
            }
          }}
        />
      )}

      {/* ── Password modal (bulk) ───────────────────────────────────────── */}
      {bulkPwdOpen && (
        <PasswordModal
          title={t('users.passwordModal.bulkTitle', '', { count: selectedCount })}
          onClose={() => setBulkPwdOpen(false)}
          onSubmit={async (pw) => { await bulkSetPassword(pw); }}
        />
      )}

      {/* ── Short-code modal ────────────────────────────────────────────── */}
      {shortModalFor && (
        <ShortCodeModal
          current={shortModalFor.cardsShortIds?.[0] ?? ''}
          onClose={() => setShortModalFor(null)}
          onSubmit={async (code) => {
            await setShortCode(shortModalFor, code);
            setShortModalFor(null);
          }}
        />
      )}

      {/* ── Multi-card edit ────────────────────────────────────────────── */}
      <MultiCardEdit
        isOpen={multiEditOpen}
        onClose={() => setMultiEditOpen(false)}
        selectedUsers={selectedUserObjects}
        onSaved={() => void refreshAll()}
      />

      {/* ── Delete confirm ──────────────────────────────────────────────── */}
      {confirmDelete && (
        <GlassModal
          title={
            confirmDelete.isBulk
              ? t('users.deleteModal.bulkTitle', '', { count: confirmDelete.ids.length })
              : t('users.deleteModal.singleTitle')
          }
          onClose={() => setConfirmDelete(null)}
        >
          <p className="text-sm text-ink mb-5">
            {t('users.deleteModal.description')}
          </p>
          {bulkError && <p className="text-xs text-danger-400 mb-3">{bulkError}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg border border-line bg-panel px-4 py-2 text-sm text-ink hover:bg-raised hover:text-ink transition-all"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={() => deleteUsers(confirmDelete.ids)}
              disabled={busyBulk}
              className="inline-flex items-center gap-1.5 rounded-lg bg-danger-500 hover:bg-danger-600 px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50"
            >
              {busyBulk ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {t('users.deleteModal.confirm')}
            </button>
          </div>
        </GlassModal>
      )}
    </Layout>
  );
}

// ─── Password sub-modal ─────────────────────────────────────────────────────
function PasswordModal({
  title,
  onClose,
  onSubmit,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (pw: string) => Promise<void>;
}) {
  const { t } = useAppTranslation();
  const [pw, setPw] = useState(generatePassword());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (pw.length < 8) {
      setErr(t('users.passwordModal.minLength'));
      return;
    }
    setBusy(true);
    try {
      await onSubmit(pw);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <GlassModal title={title} onClose={onClose}>
      <form onSubmit={submit}>
        <label className={labelCls}>{t('users.passwordModal.newPassword')}</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className={inputCls}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setPw(generatePassword())}
            className={btnSmGhost}
            title={t('users.passwordModal.regenerate')}
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {err && <p className="mt-2 text-xs text-danger-400">{err}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-line bg-panel px-4 py-2 text-sm text-ink hover:bg-raised hover:text-ink transition-all"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {t('common.save')}
          </button>
        </div>
      </form>
    </GlassModal>
  );
}

// ─── Short-code sub-modal ───────────────────────────────────────────────────
function ShortCodeModal({
  current,
  onClose,
  onSubmit,
}: {
  current: string;
  onClose: () => void;
  onSubmit: (code: string) => Promise<void>;
}) {
  const { t } = useAppTranslation();
  const [code, setCode] = useState(current);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (!/^[a-zA-Z0-9]{7}$/.test(code)) {
      setErr(t('users.shortCodeModal.validation'));
      return;
    }
    setBusy(true);
    try {
      await onSubmit(code);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <GlassModal title={t('users.shortCodeModal.title')} onClose={onClose}>
      <form onSubmit={submit}>
        <label className={labelCls}>{t('users.shortCodeModal.label')}</label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.trim())}
          maxLength={7}
          className={`${inputCls} font-mono tracking-wider`}
          autoFocus
        />
        {err && <p className="mt-2 text-xs text-danger-400">{err}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-line bg-panel px-4 py-2 text-sm text-ink hover:bg-raised hover:text-ink transition-all"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {t('common.save')}
          </button>
        </div>
      </form>
    </GlassModal>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function RoleBadge({ role }: { role: string }) {
  const roleLabel = useRoleLabel();
  const map: Record<string, string> = {
    owner:           'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300 border-accent-200 dark:border-accent-500/30',
    admin:           'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300 border-accent-200 dark:border-accent-500/30',
    department_lead: 'bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-300 border-warning-200 dark:border-warning-500/30',
    member:          'bg-raised text-ink-muted border-line',
    superadmin:      'bg-danger-100 dark:bg-danger-500/20 text-danger-700 dark:text-danger-300 border-danger-200 dark:border-danger-500/30',
  };
  return (
    <span
      className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
        map[role] ?? map['member']
      }`}
    >
      {roleLabel(role)}
    </span>
  );
}

function generatePassword(): string {
  return (
    Math.random().toString(36).substring(2, 10) +
    Math.random().toString(36).substring(2, 5).toUpperCase()
  );
}
